
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bot } from "lucide-react";
import { LoginForm } from "@/components/auth/LoginForm";
import { useToast } from "@/hooks/use-toast";

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [logoName] = useState(() => localStorage.getItem("logoName") || "MRS IA");
  const [logoUrl] = useState(() => localStorage.getItem("logoUrl") || "");
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    let mounted = true;
    let sessionCheckTimer: NodeJS.Timeout;

    const checkSession = async () => {
      try {
        console.log("Login: Checking session...");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Login: Error checking session:", error);
          setErrorMessage("Erro ao verificar sessão. Por favor, tente novamente.");
          return;
        }

        const session = data?.session;

        if (session && mounted) {
          console.log("Login: Session found, checking user data");
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('email', session.user.email)
            .maybeSingle();

          if (userError) {
            console.error("Login: Error fetching user data:", userError);
            await supabase.auth.signOut();
            setErrorMessage("Erro ao verificar usuário no sistema.");
            return;
          }

          if (!userData && mounted) {
            console.log("Login: No user data found, signing out");
            await supabase.auth.signOut();
            setErrorMessage("Usuário não encontrado no sistema.");
            return;
          }

          // Armazenar company ID para uso futuro
          if (userData.company_id) {
            localStorage.setItem('currentCompanyId', userData.company_id);
            if (session.user.id) {
              localStorage.setItem(`companyId_${session.user.id}`, userData.company_id);
            }
            console.log("Login: Company ID saved:", userData.company_id);
          }

          console.log("Login: Valid session found, redirecting to home");
          navigate("/", { replace: true });
        }
      } catch (error) {
        console.error("Login: Error during session check:", error);
        setErrorMessage("Erro ao verificar sessão. Por favor, tente novamente.");
      } finally {
        if (mounted) {
          setIsLoading(false);
          setSessionChecked(true);
        }
      }
    };

    checkSession();

    // Verificar sessão periodicamente para recuperação automática
    if (mounted && !sessionCheckTimer) {
      sessionCheckTimer = setInterval(async () => {
        try {
          console.log("Login: Periodic session check");
          const { data } = await supabase.auth.getSession();
          
          if (data.session && mounted && sessionChecked) {
            console.log("Login: Valid session found during periodic check, redirecting to home");
            navigate("/", { replace: true });
          }
        } catch (err) {
          console.error("Login: Error in periodic session check:", err);
        }
      }, 30000); // Verificar a cada 30 segundos
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log("Login: Auth state changed:", event);
      if (event === 'SIGNED_IN' && session) {
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('email', session.user.email)
            .maybeSingle();

          if (userError) {
            console.error("Login: Error fetching user data:", userError);
            await supabase.auth.signOut();
            setErrorMessage("Erro ao verificar usuário no sistema.");
            return;
          }

          if (!userData) {
            console.error("Login: User not found in database");
            await supabase.auth.signOut();
            setErrorMessage("Usuário não encontrado no sistema.");
            return;
          }

          // Armazenar company ID para uso futuro
          if (userData.company_id) {
            localStorage.setItem('currentCompanyId', userData.company_id);
            if (session.user.id) {
              localStorage.setItem(`companyId_${session.user.id}`, userData.company_id);
            }
            console.log("Login: Company ID saved on sign in:", userData.company_id);
          }

          console.log("Login: Authentication successful");
          toast({
            title: "Login realizado com sucesso!",
            description: `Bem-vindo, ${userData.name}!`,
          });
          navigate("/", { replace: true });
        } catch (error) {
          console.error("Login: Error during authentication:", error);
          setErrorMessage("Erro durante autenticação. Por favor, tente novamente.");
        }
      }
    });

    return () => {
      mounted = false;
      if (sessionCheckTimer) clearInterval(sessionCheckTimer);
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        <Card>
          <CardHeader className="space-y-1 flex flex-col items-center">
            <div className="w-20 h-20 mb-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      const bot = document.createElement('div');
                      bot.innerHTML = '<Bot className="w-full h-full text-primary" />';
                      parent.appendChild(bot);
                    }
                  }}
                />
              ) : (
                <Bot className="w-full h-full text-primary" />
              )}
            </div>
            <CardTitle className="text-2xl text-center">{logoName}</CardTitle>
          </CardHeader>
          <CardContent>
            <LoginForm />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
